import { PlugsIcon } from '@phosphor-icons/react'
import {
  AlertBanner,
  Button,
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
  Skeleton,
  TextInput,
} from '@curri/components'
import { useState, type FormEvent } from 'react'

import type { Resource } from '../../api/client.ts'
import {
  putWebhookEndpoint,
  type WebhookEndpointConfig,
  type WebhookVariant,
} from '../../api/webhooks.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'

import {
  describeFailure,
  isLegacySource,
  LEGACY_SOURCE_NOTE,
  VARIANT_LABELS,
} from './format.ts'
import { Panel, PANEL_HAIRLINE } from './Panel.tsx'
import { Pill } from './Pill.tsx'
import { SecretReveal } from './SecretReveal.tsx'
import {
  isSignedVariant,
  variantsOf,
  variantTone,
  WEBHOOK_TYPES,
  type WebhookTypeDef,
  type WebhookTypeId,
} from './types.ts'

// What Curri would actually POST to, resolved the same way the sender resolves
// it. An endpoint set on the legacy account column counts; an empty-string URL
// does not, and the API filters it out before it reaches here — so an endpoint
// missing from this list is one that would never fire.
//
// Editing (INT-402): a session holding `webhook:write` can repoint any variant,
// or configure one that has never fired. `canEdit` is decided by the parent —
// write scope AND not ghosting — but the server enforces both independently, so
// hiding the controls is UX, not the security boundary.
//
// One card renders one webhook type (INT-531); which variants it lists comes
// from the registry in ./types.ts, so a new type adds rows here by existing.

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'minted'; secret: string }
  | { kind: 'failed'; message: string }

const rowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 14,
  padding: '14px 0',
  borderTop: `1px solid ${PANEL_HAIRLINE}`,
} as const

const VariantPill = ({ variant }: { variant: WebhookVariant }) => (
  <Pill tone={variantTone(variant)}>{VARIANT_LABELS[variant]}</Pill>
)

type HeaderChange =
  | { kind: 'unchanged' }
  | { kind: 'removed' }
  | { kind: 'set'; name: string; value: string }

/**
 * What to send for the auth header, given the form's state.
 *
 * The asymmetry driving this: Curri returns the header *name* but never the
 * value, so the form can prefill a name and can never prefill a value. That
 * makes "leave the value blank" mean *keep the stored credential* rather than
 * "clear it" — and it makes setting a name without a value impossible to honour,
 * because the sender needs both before it sends either.
 *
 * Returns a string instead when the combination cannot be saved, so the caller
 * can show it under the field rather than guessing on the partner's behalf.
 */
export const resolveHeaderChange = ({
  storedName,
  name,
  value,
  remove,
}: {
  storedName: string | null
  name: string
  value: string
  remove: boolean
}): HeaderChange | string => {
  if (remove) return { kind: 'removed' }

  const trimmedName = name.trim()
  if (value !== '') {
    if (trimmedName === '') return 'Enter the header name as well as its value.'
    return { kind: 'set', name: trimmedName, value }
  }

  // A name typed with no value: the stored value belongs to the old name, so
  // reusing it under a new one would send a credential the partner never paired
  // with it.
  if (trimmedName !== (storedName ?? '')) {
    return storedName === null
      ? 'Enter the header value too — Curri never stores a name without one.'
      : 'Renaming the header needs its value again, because Curri never returns the stored one.'
  }

  return { kind: 'unchanged' }
}

/**
 * The `authHeader` field for the request body — absent, null, or the pair.
 * Absent and null are different instructions to the API (keep vs remove), so
 * they cannot collapse into one.
 */
const headerBody = (
  change: HeaderChange
): { authHeader?: { name: string; value: string } | null } => {
  switch (change.kind) {
    case 'unchanged':
      return {}
    case 'removed':
      return { authHeader: null }
    case 'set':
      return { authHeader: { name: change.name, value: change.value } }
    default:
      throw new Error(`Unhandled header change: ${JSON.stringify(change)}`)
  }
}

/**
 * Inline editor for one variant: its URL and the header Curri authenticates
 * with. Exported because the webhook docs' getting-started flow embeds the
 * same editor (src/docs/components/WebhookEndpointSetup.tsx) — one form, one
 * set of header rules, wherever an endpoint gets configured.
 */
export const EndpointEditor = ({
  variant,
  initialUrl,
  storedHeaderName,
  onCancel,
  onSaved,
}: {
  variant: WebhookVariant
  initialUrl: string
  /** The stored header's name, or null when there is none. Never its value. */
  storedHeaderName: string | null
  onCancel: () => void
  onSaved: () => void
}) => {
  const { apiFetch } = useAuth()
  const [url, setUrl] = useState(initialUrl)
  const [headerName, setHeaderName] = useState(storedHeaderName ?? '')
  const [headerValue, setHeaderValue] = useState('')
  const [removeHeader, setRemoveHeader] = useState(false)
  const [save, setSave] = useState<SaveState>({ kind: 'idle' })

  const trimmedUrl = url.trim()
  const isNewEndpoint = initialUrl === ''
  const urlChanged = trimmedUrl !== initialUrl

  const clearError = () => {
    // Keeps a stale rejection from sitting under a value that has since changed.
    if (save.kind === 'failed') setSave({ kind: 'idle' })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (save.kind === 'saving') return

    const fail = (message: string) => setSave({ kind: 'failed', message })

    // The same rule the API enforces, checked here so the common mistake gets an
    // instant answer instead of a round-trip. Only when the URL is actually being
    // sent — an untouched http:// legacy endpoint can still have its header
    // changed.
    if ((isNewEndpoint || urlChanged) && !trimmedUrl.startsWith('https://')) {
      fail(
        'The endpoint must be an https:// URL — webhook payloads carry addresses and contact details.'
      )
      return
    }

    const header = resolveHeaderChange({
      storedName: storedHeaderName,
      name: headerName,
      value: headerValue,
      remove: removeHeader,
    })
    if (typeof header === 'string') {
      fail(header)
      return
    }

    setSave({ kind: 'saving' })
    void (async () => {
      const result = await putWebhookEndpoint(apiFetch, {
        variant,
        // Omitted when untouched, so changing only the header cannot fail
        // validation on a URL the partner never edited.
        ...(isNewEndpoint || urlChanged ? { url: trimmedUrl } : {}),
        ...headerBody(header),
      })
      if (!result.ok) {
        setSave({ kind: 'failed', message: describeFailure(result.failure).body })
        return
      }
      // This response is the only one that carries the new secret, so the save
      // is not finished until the partner has taken it.
      const secret = result.data.signingSecret
      if (secret !== undefined) {
        setSave({ kind: 'minted', secret })
        return
      }
      // The parent refetches /config rather than trusting this response, so the
      // card always shows what the server now holds.
      onSaved()
    })()
  }

  const headerTouched =
    removeHeader ||
    headerValue !== '' ||
    headerName.trim() !== (storedHeaderName ?? '')
  const nothingToSave = !urlChanged && !headerTouched
  const missingUrl = isNewEndpoint && trimmedUrl === ''

  if (save.kind === 'minted') {
    return (
      <div
        style={{ padding: '14px 0', borderTop: `1px solid ${PANEL_HAIRLINE}` }}
      >
        <SecretReveal onDone={onSaved} secret={save.secret} />
      </div>
    )
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '14px 0',
        borderTop: `1px solid ${PANEL_HAIRLINE}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <VariantPill variant={variant} />
      </div>
      <TextInput
        helperText="Takes effect on the next webhook Curri sends."
        label={`${VARIANT_LABELS[variant]} endpoint URL`}
        onChange={event => {
          setUrl(event.target.value)
          clearError()
        }}
        placeholder="https://api.example.com/webhooks/curri"
        value={url}
      />
      {isSignedVariant(variant) && (
        <p
          style={{
            margin: 0,
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--curri-text-tertiary)',
          }}
        >
          Curri signs every request to this endpoint. Saving a new endpoint
          issues its signing secret, shown once.{' '}
          <a href="/docs/webhooks/security">How to verify a signature</a>
        </p>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          opacity: removeHeader ? 0.5 : 1,
        }}
      >
        <div style={{ flex: '1 1 180px' }}>
          <TextInput
            disabled={removeHeader}
            helperText="Optional. Sent on every webhook so your server can authenticate the call."
            label="Auth header name"
            onChange={event => {
              setHeaderName(event.target.value)
              clearError()
            }}
            placeholder="X-Api-Key"
            value={headerName}
          />
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <TextInput
            disabled={removeHeader}
            helperText={
              storedHeaderName === null
                ? 'Stored encrypted. Curri never shows it again.'
                : 'Leave blank to keep the current value.'
            }
            label="Auth header value"
            onChange={event => {
              setHeaderValue(event.target.value)
              clearError()
            }}
            placeholder={storedHeaderName === null ? '' : '••••••••'}
            type="password"
            value={headerValue}
          />
        </div>
      </div>

      {storedHeaderName !== null && (
        <div>
          <Button
            label={removeHeader ? 'Keep auth header' : 'Remove auth header'}
            onClick={() => {
              setRemoveHeader(current => !current)
              clearError()
            }}
            size="small"
            type="button"
            variant="tertiary"
          />
          {removeHeader && (
            <span
              style={{
                marginLeft: 10,
                fontSize: 12.5,
                color: 'var(--curri-text-tertiary)',
              }}
            >
              Curri will POST without an auth header when you save.
            </span>
          )}
        </div>
      )}

      {save.kind === 'failed' && (
        <AlertBanner title="That change was not saved" type="danger">
          {save.message}
        </AlertBanner>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button
          disabled={save.kind === 'saving'}
          label="Cancel"
          onClick={onCancel}
          size="base"
          type="button"
          variant="tertiary"
        />
        <Button
          disabled={missingUrl || nothingToSave}
          label="Save endpoint"
          loading={save.kind === 'saving'}
          size="base"
          type="submit"
          variant="secondary"
        />
      </div>
    </form>
  )
}

type RotateState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'rotating' }
  | { kind: 'minted'; secret: string }
  | { kind: 'failed'; message: string }

/**
 * Whether this endpoint has a signing secret, and the rotation that replaces
 * it.
 *
 * Two steps rather than one because there is no undo: the stored secret stops
 * verifying the moment the new one lands, and the new one is readable exactly
 * once — so the confirm says what breaks and the reveal is the only place a
 * secret is ever rendered.
 */
const SigningSecret = ({
  endpoint,
  onRotated,
}: {
  endpoint: WebhookEndpointConfig
  /** Present when the session may rotate. Called once the new secret is taken. */
  onRotated?: () => void
}) => {
  const { apiFetch } = useAuth()
  const [state, setState] = useState<RotateState>({ kind: 'idle' })

  const rotate = () => {
    setState({ kind: 'rotating' })
    void (async () => {
      const result = await putWebhookEndpoint(apiFetch, {
        variant: endpoint.variant,
        rotateSigningSecret: true,
      })
      if (!result.ok) {
        setState({
          kind: 'failed',
          message: describeFailure(result.failure).body,
        })
        return
      }
      const secret = result.data.signingSecret
      if (secret === undefined) {
        setState({ kind: 'idle' })
        onRotated?.()
        return
      }
      setState({ kind: 'minted', secret })
    })()
  }

  if (state.kind === 'minted') {
    return (
      <div style={{ flexBasis: '100%' }}>
        <SecretReveal
          onDone={() => {
            setState({ kind: 'idle' })
            onRotated?.()
          }}
          secret={state.secret}
        />
      </div>
    )
  }

  return (
    <>
      <Pill tone={endpoint.hasSigningSecret ? 'success' : 'neutral'}>
        {endpoint.hasSigningSecret
          ? 'Signing secret · set'
          : 'Signing secret · not set'}
      </Pill>
      {onRotated && state.kind !== 'confirming' && (
        <Button
          label={endpoint.hasSigningSecret ? 'Rotate secret' : 'Create secret'}
          loading={state.kind === 'rotating'}
          onClick={() => setState({ kind: 'confirming' })}
          size="small"
          type="button"
          variant="tertiary"
        />
      )}
      {state.kind === 'confirming' && (
        <div
          style={{
            flexBasis: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span
            style={{
              flex: '1 1 260px',
              fontSize: 12.5,
              lineHeight: 1.5,
              color: 'var(--curri-text-secondary)',
            }}
          >
            Rotating invalidates the current secret the moment it succeeds; your
            handler will reject Curri&apos;s signatures until you deploy the new
            one.
          </span>
          <Button
            label="Rotate now"
            onClick={rotate}
            size="small"
            type="button"
            variant="secondary"
          />
          <Button
            label="Cancel"
            onClick={() => setState({ kind: 'idle' })}
            size="small"
            type="button"
            variant="tertiary"
          />
        </div>
      )}
      {state.kind === 'failed' && (
        <div style={{ flexBasis: '100%' }}>
          <AlertBanner title="The secret was not rotated" type="danger">
            {state.message}
          </AlertBanner>
        </div>
      )}
    </>
  )
}

const EndpointRow = ({
  endpoint,
  onEdit,
  onRotated,
}: {
  endpoint: WebhookEndpointConfig
  onEdit?: () => void
  onRotated?: () => void
}) => (
  <div style={rowStyle}>
    <VariantPill variant={endpoint.variant} />
    <code
      style={{
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 13,
        color: 'var(--curri-text)',
        overflowWrap: 'anywhere',
      }}
    >
      {endpoint.url}
    </code>
    {isSignedVariant(endpoint.variant) && (
      <SigningSecret endpoint={endpoint} onRotated={onRotated} />
    )}
    <span style={{ flex: '1 1 24px' }} />
    <span
      style={{
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11.5,
        color: endpoint.authHeaderName
          ? 'var(--curri-text-secondary)'
          : 'var(--curri-text-tertiary)',
      }}
    >
      {/* The header *name* only. Curri never returns the value — not masked,
          absent — because it is a credential your server checks. */}
      {endpoint.authHeaderName
        ? `Auth header: ${endpoint.authHeaderName}`
        : 'No auth header'}
      {/* Only the legacy source is annotated; see LEGACY_SOURCE_NOTE. */}
      {isLegacySource(endpoint.source) && ` · ${LEGACY_SOURCE_NOTE}`}
    </span>
    {onEdit && (
      <Button
        label="Edit"
        onClick={onEdit}
        size="small"
        type="button"
        variant="tertiary"
      />
    )}
  </div>
)

/** An editable slot for a variant nothing is configured for. */
const MissingEndpointRow = ({
  variant,
  onAdd,
}: {
  variant: WebhookVariant
  onAdd: () => void
}) => (
  <div style={rowStyle}>
    <VariantPill variant={variant} />
    <span style={{ fontSize: 13, color: 'var(--curri-text-tertiary)' }}>
      Not configured
    </span>
    <span style={{ flex: '1 1 24px' }} />
    {/* The pill beside it names the variant on screen; the label names it to a
        screen reader, which would otherwise hear two identical buttons. */}
    <Button
      aria-label={`Add endpoint for ${VARIANT_LABELS[variant]}`}
      label="Add endpoint"
      onClick={onAdd}
      size="small"
      type="button"
      variant="tertiary"
    />
  </div>
)

const NoEndpoints = () => (
  <EmptyState size="small">
    <EmptyStateIcon>
      <PlugsIcon />
    </EmptyStateIcon>
    <EmptyStateTitle>No webhook endpoint configured</EmptyStateTitle>
    <EmptyStateDescription>
      Configuring one from this page needs a session with the webhook:write scope
      — sign out and back in to request it, or email engineering@curri.com.
    </EmptyStateDescription>
  </EmptyState>
)

/** The editable listing: every variant gets a row, configured or not. */
const EditableEndpoints = ({
  endpoints,
  onSaved,
  variants,
}: {
  endpoints: WebhookEndpointConfig[]
  onSaved: () => void
  variants: readonly WebhookVariant[]
}) => {
  const [editing, setEditing] = useState<WebhookVariant | null>(null)

  return (
    <>
      {variants.map(variant => {
        const endpoint = endpoints.find(e => e.variant === variant)

        if (editing === variant) {
          return (
            <EndpointEditor
              initialUrl={endpoint?.url ?? ''}
              key={variant}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null)
                onSaved()
              }}
              storedHeaderName={endpoint?.authHeaderName ?? null}
              variant={variant}
            />
          )
        }

        return endpoint ? (
          <EndpointRow
            endpoint={endpoint}
            key={variant}
            onEdit={() => setEditing(variant)}
            onRotated={onSaved}
          />
        ) : (
          <MissingEndpointRow
            key={variant}
            onAdd={() => setEditing(variant)}
            variant={variant}
          />
        )
      })}
    </>
  )
}

export const EndpointsCard = ({
  resource,
  canEdit,
  onSaved,
  type = 'deliveries',
}: {
  resource: Resource<WebhookEndpointConfig[]>
  /** Write scope granted and not ghosting. The server enforces both anyway. */
  canEdit: boolean
  onSaved: () => void
  type?: WebhookTypeId
}) => {
  const variants = variantsOf(type)
  // Typed as the registry's row shape: `WEBHOOK_TYPES` is `as const`, so
  // indexing it by a variable is a union of the members, and `note` is optional.
  const { description, note }: WebhookTypeDef = WEBHOOK_TYPES[type]
  // The read returns every type's endpoints; this card answers for one of them.
  const endpoints =
    resource.status === 'ready'
      ? resource.data.filter(endpoint => variants.includes(endpoint.variant))
      : []

  return (
    <Panel style={{ paddingBottom: 10 }}>
      <Panel.Title
        meta={
          resource.status === 'ready'
            ? `${endpoints.length} configured`
            : undefined
        }
      >
        Endpoints
      </Panel.Title>
      <Panel.Description>{description}</Panel.Description>
      {note !== undefined && (
        <p
          style={{
            margin: '6px 0 0',
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--curri-text-tertiary)',
          }}
        >
          {note}
        </p>
      )}

      <div style={{ marginTop: 14 }}>
        {resource.status === 'loading' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              paddingBottom: 10,
            }}
          >
            <Skeleton style={{ height: 20 }} />
            <Skeleton style={{ height: 20, width: '60%' }} />
          </div>
        )}

        {resource.status === 'failed' &&
          (() => {
            const copy = describeFailure(resource.failure)
            return (
              <AlertBanner title={copy.title} type={copy.tone}>
                {copy.body}
              </AlertBanner>
            )
          })()}

        {resource.status === 'ready' &&
          (canEdit ? (
            <EditableEndpoints
              endpoints={endpoints}
              onSaved={onSaved}
              variants={variants}
            />
          ) : endpoints.length === 0 ? (
            <NoEndpoints />
          ) : (
            endpoints.map(endpoint => (
              <EndpointRow endpoint={endpoint} key={endpoint.variant} />
            ))
          ))}
      </div>
    </Panel>
  )
}
